import { finalize, catchError, map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { InvoicePeriodItem } from '../../../invoice/model/invoice-period';
import { LocalDate } from '../../../core/model/local-date';
import { VatReport } from '../../model/vat-report';

@Injectable()
export class ReportService {
  constructor(
    private _client: HttpClient,
    private _messageService: MessageService,
    private _progressBar: ProgressBarService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    this._endpoint = `${config.apiEndpointV2}/reports`;
  }

  private readonly _endpoint: string;

  getAccountBalancePerInvoice(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client.post<any[]>(`${this._endpoint}/account-balance-per-invoice`, JSON.stringify(filter)).pipe(
      catchError(error => {
        this._messageService.showError(error);
        return [];
      }),
      finalize(() => this._progressBar.stop())
    );
  }

  getAccountBalancePerInvoiceExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/account-balance-per-invoice-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'AccountBalancePerInvoice.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getInvoicedPerRentalObjectExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/invoiced-per-rental-object-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Aviseringsjournal.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getBrfInnerFundPerRentalObjectExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/brf-inner-fund-per-rental-object-excel`, JSON.stringify(filter), {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Inre fond.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getCheckInAndCheckOutExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/checkin-checkout-report-excel`, JSON.stringify(filter), {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'In-avflyttningslista.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getMembershipList(companyId: string, reportDate: LocalDate): Observable<any[]> {
    this._progressBar.start();
    return this._client.get<any[]>(`${this._endpoint}/membership-list/${companyId}?reportDate=${reportDate}`).pipe(
      catchError(error => {
        this._messageService.showError(error);
        return [];
      }),
      finalize(() => this._progressBar.stop())
    );
  }

  getMembershipListExcel(companyId: string, reportDate: LocalDate): Observable<File> {
    this._progressBar.start();
    return this._client
      .get(`${this._endpoint}/membership-list-excel/${companyId}?reportDate=${reportDate}`, { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Membership List.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getRentalObjectRentLoss(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/rentalobject-rentloss`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getReasonablenessControlReport(filter: any): Observable<any> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/reasonableness-report`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getReasonablenessControlReportExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/reasonableness-report-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Kontrollrapport.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getStaircaseListReportWord(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/staircase-report-word`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          if (res.status == 204) {
            this._messageService.showMessage('REPORT.ERRORS.MISSING_CONTRACTS');
            return;
          }
          return this.toFile(res, 'Trapphuslista.docx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getTaxReductionReport(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/tax-reduction-report-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Skattereduktion.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getTaxReductionReportXML(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/report-tax-reduction-xml`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'RutAvdrag.xml');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getMonthAnalysisAmountReport(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/month-analysis-amount-report`, JSON.stringify(filter), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getInvoicedPerRentalObjectReport(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/invoiced-per-rental-object-report`, JSON.stringify(filter), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getBrfInnerFundPerRentalObjectReport(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/brf-inner-fund-per-rental-object-report`, JSON.stringify(filter), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getVoluntaryTaxLiabilityReport(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/voluntary-tax-liability`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getVoluntaryTaxLiabilityReportExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/voluntary-tax-liability-report-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Frivillig beskattning.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getMonthAnalysisExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/monthanalysis-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Manadsanalys.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getRentRollExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/rentroll-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'RentRoll.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getNotFullyPaidInvoicesExcel(filter: any): Observable<File> {
    this._progressBar.start();
    return this._client
      .post(`${this._endpoint}/not-fully-paid-invoices-excel`, JSON.stringify(filter), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Fakturor.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getNotFullyPaidInvoicesReport(filter: any): Observable<any[]> {
    this._progressBar.start();
    return this._client
      .post<any[]>(`${this._endpoint}/not-fully-paid-invoices-report`, JSON.stringify(filter), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getRentRollReport(filter: any): Observable<any> {
    this._progressBar.start();
    return this._client
      .post<any>(`${this._endpoint}/rentroll-report`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getCheckInCheckOutReport(filter: any): Observable<any> {
    this._progressBar.start();
    return this._client
      .post<any>(`${this._endpoint}/checkin-checkout-report`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getPeriodFilterDefinition(): Observable<{
    dateLimits: InvoicePeriodItem[];
  }> {
    return this._client.get<any>(`${this._endpoint}/period-filter`).pipe(
      map(res => {
        return {
          dateLimits: Array.isArray(res.dateLimits) ? res.dateLimits.map(x => Object.assign(new InvoicePeriodItem(), x)) : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getVatReport(companyId: string, from: LocalDate, to: LocalDate): Observable<VatReport> {
    this._progressBar.start();
    return this._client
      .get<any>(`${this._endpoint}/vat-report/${companyId}/${from.year}/${from.month}/${to.year}/${to.month}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map(result => new VatReport(result)),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  getVatReportExcel(companyId: string, from: LocalDate, to: LocalDate): Observable<File> {
    this._progressBar.start();
    return this._client
      .get(`${this._endpoint}/vat-report-excel/${companyId}/${from.year}/${from.month}/${to.year}/${to.month}`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Momsavstamning.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this._progressBar.stop())
      );
  }

  protected toFile(response: HttpResponse<Blob>, defaultName: string): File {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const matches = /filename=([^;]+)/gi.exec(contentDisposition);
    const fileName = (matches ? matches[1].replace(/['"]+/g, '') : defaultName).trim();
    return new File([response.body], fileName, { type: response.body.type });
  }
}
